import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    FormControlLabel,
    Switch,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';

const initialState = {
    title: '',
    description: '',
    startsAt: '',
    endsAt: '',
    active: true,
    banner: null,
    placeName: '',
    placeStreet: '',
    placeNumber: '',
    placeNeighborhood: '',
    placeCity: '',
    placeZip: '',
};

function pad2(n) {
    return String(n).padStart(2, '0');
}

function arrayToDatetimeLocal(arr) {
    if (!arr || !Array.isArray(arr) || arr.length < 5) return '';
    const [y, m, d, hh, mm] = arr;
    return `${y}-${pad2(m)}-${pad2(d)}T${pad2(hh)}:${pad2(mm)}`;
}

function normalizeLocalDateTime(value) {
    if (!value) return '';
    return value.length === 16 ? `${value}:00` : value;
}

export default function EditEventDialog({
    open,
    onClose,
    onSuccess,
    user,
    event,
}) {
    const [formData, setFormData] = useState(initialState);
    const [existingBannerUrl, setExistingBannerUrl] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasPlace, setHasPlace] = useState(false);

    // When user selects a new file, show preview from file
    const newBannerPreview = useMemo(() => {
        if (!formData.banner) return null;
        return URL.createObjectURL(formData.banner);
    }, [formData.banner]);

    useEffect(() => {
        if (!open) return;
        if (!event) return;

        const hasAnyPlace =
            !!event.placeName ||
            !!event.placeStreet ||
            !!event.placeNumber ||
            !!event.placeNeighborhood ||
            !!event.placeCity ||
            !!event.placeZip ||
            !!event.placeState;

        setHasPlace(hasAnyPlace);

        setFormData({
            title: event.title || '',
            description: event.description || '',
            startsAt: arrayToDatetimeLocal(event.startsAt),
            endsAt: arrayToDatetimeLocal(event.endsAt),
            active: event.active ?? true,
            banner: null,

            // campos do local (se vierem do backend)
            placeName: event.placeName || '',
            placeStreet: event.placeStreet || '',
            placeNumber: event.placeNumber || '',
            placeNeighborhood: event.placeNeighborhood || '',
            placeCity: event.placeCity || '',
            placeZip: event.placeZip || '',
            placeState: event.placeState || '',
        });

        setExistingBannerUrl(event.bannerUrl || '');
        setError(null);
        setSubmitting(false);
    }, [open, event]);

    // avoid memory leak for objectURL
    useEffect(() => {
        return () => {
            if (newBannerPreview) URL.revokeObjectURL(newBannerPreview);
        };
    }, [newBannerPreview]);

    const resetForm = () => {
        setFormData(initialState);
        setExistingBannerUrl('');
        setHasPlace(false);
        setError(null);
        setSubmitting(false);
    };

    const handleClose = (evt, reason) => {
        if (reason === 'backdropClick') return;
        resetForm();
        onClose?.(evt, reason);
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files?.[0] || null : value,
        }));
    };

    const validate = () => {
        if (!formData.title.trim()) return 'Informe um título.';
        if (!formData.description.trim()) return 'Informe uma descrição.';

        if (!formData.startsAt && formData.endsAt) {
            return 'Informe a data de início (startsAt).';
        }

        if (formData.startsAt && formData.endsAt) {
            const s = new Date(formData.startsAt);
            const e = new Date(formData.endsAt);
            if (e < s) return 'A data final não pode ser menor que a inicial.';
        }

        return null;
    };

    const clearSelectedBanner = () => {
        setFormData((prev) => ({ ...prev, banner: null }));
    };

    // (Optional) only send changed fields
    const appendIfChanged = (fd, key, newValue, oldValue) => {
        const nv = newValue ?? '';
        const ov = oldValue ?? '';
        if (nv !== ov) fd.append(key, nv);
    };

    const handleSubmit = async () => {
        setError(null);
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!event?.id) {
            setError('Evento inválido (sem id).');
            return;
        }

        try {
            setSubmitting(true);

            const fd = new FormData();

            // If you prefer "send everything", just append all fields like create.
            // Here I'll send only changed fields (safer for partial update DTO style).
            appendIfChanged(
                fd,
                'title',
                formData.title.trim(),
                event.title || ''
            );
            appendIfChanged(
                fd,
                'description',
                formData.description.trim(),
                event.description || ''
            );

            const newStarts = formData.startsAt
                ? normalizeLocalDateTime(formData.startsAt)
                : '';
            const oldStarts = arrayToDatetimeLocal(event.startsAt);
            appendIfChanged(
                fd,
                'startsAt',
                newStarts,
                oldStarts ? normalizeLocalDateTime(oldStarts) : ''
            );

            const newEnds = formData.endsAt
                ? normalizeLocalDateTime(formData.endsAt)
                : '';
            const oldEnds = arrayToDatetimeLocal(event.endsAt);
            appendIfChanged(
                fd,
                'endsAt',
                newEnds,
                oldEnds ? normalizeLocalDateTime(oldEnds) : ''
            );

            // active (boolean) -> string
            if ((event.active ?? true) !== formData.active) {
                fd.append('active', String(formData.active));
            }

            // banner only if user selected a new file
            if (formData.banner) {
                fd.append('banner', formData.banner);
            }

            if (hasPlace) {
                if (formData.placeName)
                    fd.append('placeName', formData.placeName);
                if (formData.placeStreet)
                    fd.append('placeStreet', formData.placeStreet);
                if (formData.placeNumber)
                    fd.append('placeNumber', formData.placeNumber);
                if (formData.placeNeighborhood)
                    fd.append('placeNeighborhood', formData.placeNeighborhood);
                if (formData.placeCity)
                    fd.append('placeCity', formData.placeCity);
                if (formData.placeState)
                    fd.append('placeState', formData.placeState);
                if (formData.placeZip) fd.append('placeZip', formData.placeZip);
            }

            await RequestService.updateEvent(event.id, fd);

            onSuccess?.('Evento atualizado com sucesso!');
            resetForm();
            onClose?.();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data ||
                'Erro ao atualizar o evento.';
            setError(
                typeof message === 'string'
                    ? message
                    : 'Erro ao atualizar o evento.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const bannerToShow = newBannerPreview || existingBannerUrl;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Editar Evento
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: '#F5F5F5',
                            padding: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                border: '1px dashed #CCCCCC',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                overflow: 'hidden',
                            }}
                        >
                            {bannerToShow ? (
                                <img
                                    src={bannerToShow}
                                    alt='Banner'
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <AddPhotoAlternateOutlinedIcon
                                    fontSize='large'
                                    color='disabled'
                                />
                            )}
                        </Box>

                        <Button
                            variant='contained'
                            component='label'
                            fullWidth
                            disabled={submitting}
                        >
                            Trocar Banner
                            <input
                                type='file'
                                accept='image/*'
                                hidden
                                name='banner'
                                onChange={handleChange}
                            />
                        </Button>

                        {formData.banner ? (
                            <Button
                                variant='text'
                                color='inherit'
                                onClick={clearSelectedBanner}
                                disabled={submitting}
                                sx={{ mt: -1 }}
                            >
                                Remover nova imagem selecionada
                            </Button>
                        ) : null}

                        <Typography variant='body2' color='text.secondary'>
                            {existingBannerUrl && !formData.banner
                                ? 'Banner atual carregado (link assinado)'
                                : 'Banner (opcional)'}
                        </Typography>
                    </Box>

                    <TextField
                        label='Título'
                        name='title'
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        disabled={submitting}
                    />

                    <TextField
                        label='Descrição'
                        name='description'
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={submitting}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label='Início'
                            name='startsAt'
                            type='datetime-local'
                            value={formData.startsAt}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: 220 }}
                            disabled={submitting}
                        />
                        <TextField
                            label='Fim'
                            name='endsAt'
                            type='datetime-local'
                            value={formData.endsAt}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: 220 }}
                            disabled={submitting}
                        />
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.active}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        active: e.target.checked,
                                    }))
                                }
                                disabled={submitting}
                            />
                        }
                        label={formData.active ? 'Ativo' : 'Inativo'}
                    />

                    <Typography sx={{ fontWeight: 600 }}>
                        Local do evento
                    </Typography>

                    <ToggleButtonGroup
                        value={hasPlace ? 'YES' : 'NO'}
                        exclusive
                        onChange={(e, v) => {
                            if (!v) return;
                            const enable = v === 'YES';
                            setHasPlace(enable);

                            // opcional: se desligou, limpa os campos
                            if (!enable) {
                                setFormData((prev) => ({
                                    ...prev,
                                    placeName: '',
                                    placeStreet: '',
                                    placeNumber: '',
                                    placeNeighborhood: '',
                                    placeCity: '',
                                    placeZip: '',
                                    placeState: '',
                                }));
                            }
                        }}
                        fullWidth
                        disabled={submitting}
                    >
                        <ToggleButton value='NO'>Sem local</ToggleButton>
                        <ToggleButton value='YES'>Inserir local</ToggleButton>
                    </ToggleButtonGroup>

                    {hasPlace ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <TextField
                                label='Nome do local'
                                name='placeName'
                                value={formData.placeName}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />

                            <TextField
                                label='Rua'
                                name='placeStreet'
                                value={formData.placeStreet}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    label='Número'
                                    name='placeNumber'
                                    value={formData.placeNumber}
                                    onChange={handleChange}
                                    sx={{ flex: 1, minWidth: 140 }}
                                    disabled={submitting}
                                />
                                <TextField
                                    label='Bairro'
                                    name='placeNeighborhood'
                                    value={formData.placeNeighborhood}
                                    onChange={handleChange}
                                    sx={{ flex: 2, minWidth: 220 }}
                                    disabled={submitting}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    label='Cidade'
                                    name='placeCity'
                                    value={formData.placeCity}
                                    onChange={handleChange}
                                    sx={{ flex: 2, minWidth: 220 }}
                                    disabled={submitting}
                                />
                                <TextField
                                    label='CEP'
                                    name='placeZip'
                                    value={formData.placeZip}
                                    onChange={handleChange}
                                    sx={{ flex: 1, minWidth: 160 }}
                                    disabled={submitting}
                                />
                            </Box>

                            <TextField
                                label='Estado'
                                name='placeState'
                                value={formData.placeState || ''}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />
                        </Box>
                    ) : null}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant='outlined'
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color='primary'
                    variant='contained'
                    disabled={submitting}
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    {submitting ? 'Salvando...' : 'Salvar alterações'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
